import { notify } from '@billy/design-system'
import { useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useCurrentUser, useUpdateUser } from '@modules-deprecated/app/user'

import { queryClient } from '../../../../../config/queryClient'
import { useDirtyRoute } from '../../../../../contexts/dirtyRouteContext'
import { NotificationKeys } from '../../../../../enums/notificationKeys'
import { QueryKeys } from '../../../../../enums/queryKeys'
import { useForm } from '../../../../../hooks'
import { getDefaultValues, getValidationSchema, UserPrivacyForm } from '../utils/userPrivacyFormData'

export const useUserPrivacyForm = () => {
  const { t } = useTranslation()
  const { setDirty: setRouteDirty } = useDirtyRoute()
  const { user, isLoading: isUserDataLoading } = useCurrentUser()
  const { update: updateUser, isLoading: isSubmitting } = useUpdateUser({
    onSuccess: () => {
      notify({
        id: NotificationKeys.SettingsUserProfile,
        message: t('settings.user.privacy.update_success'),
        variant: 'success',
      })
      setRouteDirty(false)
    },
    onError: () => {
      notify({
        id: NotificationKeys.SettingsUserProfile,
        message: t('settings.user.privacy.update_error'),
        variant: 'error',
      })
    },
    onSettled: () => {
      queryClient.invalidateQueries(QueryKeys.CurrentUser)
    },
  })

  const handleFormSubmit = useCallback(
    (values: UserPrivacyForm) => {
      if (!user) {
        return
      }
      updateUser({
        id: user.id,
        isSupportAccessAllowed: values.isSupportAccessAllowed,
      })
    },
    [updateUser, user],
  )

  const {
    formState: { isDirty: isFormDirty },
    reset,
    watch,
    ...form
  } = useForm({
    defaultValues: useMemo(() => getDefaultValues({ user }), [user]),
    validationSchema: useMemo(() => getValidationSchema(t), [t]),
    onSubmit: handleFormSubmit,
  })

  useEffect(() => {
    if (!isUserDataLoading) {
      reset(getDefaultValues({ user }))
    }
  }, [isUserDataLoading, user, reset])

  useEffect(() => {
    setRouteDirty(isFormDirty)
  }, [isFormDirty, setRouteDirty])

  return {
    ...form,
    isLoading: isUserDataLoading,
    isSubmitting,
  }
}
